import React, { useEffect, useState } from "react";
import { copyUrl } from "../../util/copyUrl";
import { Link, useParams } from "react-router-dom";
import { portfolioApi, Portfolio, PortfolioEntry } from "./portfolioApi";
import { formatCredit, marketError } from "./api";
import "../../assets/css/container/market/MarketPages.css";
import "../../assets/css/container/market/PortfolioPages.css";

function Entry({ item }: { item: PortfolioEntry }) {
  const [broken, setBroken] = useState(false);
  return <article className="market-card portfolio-card">
    {item.thumbnail && !broken ? <img src={item.thumbnail} alt="" loading="lazy" onError={() => setBroken(true)} /> : <div className="market-placeholder">YES / NO</div>}
    <div className="market-card-body">
      <span className="portfolio-result">{item.result === "VOID" ? "무효 정산" : `${item.result} 확정`}</span>
      <h2><Link to={`/markets/${item.id}`}>{item.title}</Link></h2>
      {Number(item.yesQuantity) > 0 && <p><b>YES</b> · 평균 <strong>{Number(item.yesAverage).toLocaleString("ko-KR", { maximumFractionDigits: 2 })} Credit</strong>에 매수</p>}
      {Number(item.noQuantity) > 0 && <p><b>NO</b> · 평균 <strong>{Number(item.noAverage).toLocaleString("ko-KR", { maximumFractionDigits: 2 })} Credit</strong>에 매수</p>}
      <p className="portfolio-profit">정산 순손익 <strong>{item.profit == null ? "집계 정보 없음" : `${Number(item.profit) > 0 ? "+" : ""}${formatCredit(item.profit)} Credit`}</strong></p>
    </div>
  </article>;
}
function Entries({ items }: { items: PortfolioEntry[] }) {
  return items.length ? <div className="market-grid">{items.map(item => <Entry key={item.id} item={item} />)}</div> : <p className="market-panel">아직 공개한 예측이 없습니다.</p>;
}
export function PublicPortfolio() {
  const { shareId = "" } = useParams();
  const [data, setData] = useState<Portfolio | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setData(null); setError("");
    portfolioApi.read(shareId).then(r => { if (active) setData(r.data); }).catch(e => {
      if (active) setError(e?.response?.status === 404 ? "공개된 포트폴리오를 찾을 수 없습니다. 비공개이거나 존재하지 않는 주소입니다." : marketError(e));
    });
    return () => { active = false; };
  }, [shareId]);
  useEffect(() => {
    if (!data) return;
    const title = document.title;
    document.title = `${data.nickname}의 예측 포트폴리오 | FactWeb`;
    return () => { document.title = title; };
  }, [data]);
  return <main className="MarketPages PortfolioPages">
    <header className="market-header"><div><span className="market-eyebrow">FACTWEB PORTFOLIO</span><h1>{data ? `${data.nickname}의 예측 포트폴리오` : "예측 포트폴리오"}</h1></div><Link to="/markets">예측시장 둘러보기</Link></header>
    {error ? <p role="alert">{error}</p> : !data ? <p>불러오는 중...</p> : <>
      <div className="market-panel"><p className="portfolio-introduction">{data.introduction || "실제 거래 기록으로 확인하는 나의 예측"}</p><strong>공개한 예측 {data.items.length}개</strong></div>
      <p className="portfolio-note">사용자가 선택한 예측만 표시됩니다. 평균 매수가는 전체 매수 체결의 수량 가중 평균이며, 순손익은 매도금 + 정산금 − 매수금입니다.</p>
      <Entries items={data.items} />
    </>}
  </main>;
}
export function ManagePortfolio() {
  const [settings, setSettings] = useState<Portfolio | null>(null);
  const [candidates, setCandidates] = useState<PortfolioEntry[]>([]);
  const [ids, setIds] = useState<number[]>([]);
  const [introduction, setIntroduction] = useState("");
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    let active = true;
    Promise.all([portfolioApi.mine(), portfolioApi.candidates()]).then(([mine, available]) => {
      if (!active) return;
      setSettings(mine.data); setCandidates(available.data); setIds(mine.data.items.map(i => Number(i.id)));
      setIntroduction(mine.data.introduction); setPublished(mine.data.published);
    }).catch(e => { if (active) setError(marketError(e)); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn); return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const change = () => { setDirty(true); setMessage(""); };
  const selected = ids.map(id => candidates.find(item => Number(item.id) === id)).filter((item): item is PortfolioEntry => !!item);
  const move = (index: number, delta: number) => {
    const next = [...ids]; [next[index], next[index + delta]] = [next[index + delta], next[index]]; setIds(next); change();
  };
  const save = async () => {
    setBusy(true); setError(""); setMessage("");
    try { const r = await portfolioApi.save(introduction, published, ids); setSettings(r.data); setIntroduction(r.data.introduction); setDirty(false); setMessage("저장했습니다."); }
    catch (e) { setError(marketError(e)); } finally { setBusy(false); }
  };
  const url = settings?.shareId ? `${window.location.origin}/portfolio/${settings.shareId}` : "";
  const copy = async () => {
    try { await copyUrl(url); setMessage("공유 주소를 복사했습니다."); }
    catch { setMessage("아래 공유 주소를 선택해 복사해 주세요."); }
  };
  return <main className="MarketPages PortfolioPages">
    <header className="market-header"><div><span className="market-eyebrow">MY PORTFOLIO</span><h1>포트폴리오 관리</h1></div><Link to="/markets/me">나의 예측</Link></header>
    {error && <p role="alert">{error}</p>}<p role="status">{message}</p>
    {!settings ? !error && <p>불러오는 중...</p> : <>
      <fieldset className="market-panel portfolio-settings" disabled={busy}>
        <label htmlFor="portfolio-intro">소개글 ({introduction.length}/500)</label>
        <textarea id="portfolio-intro" value={introduction} maxLength={500} rows={4} onChange={e => { setIntroduction(e.target.value); change(); }} />
        <div className="portfolio-publish-control">
          <span id="portfolio-publish-label">포트폴리오 공개</span>
          <button type="button" className="portfolio-publish-switch" role="switch" aria-checked={published}
            aria-labelledby="portfolio-publish-label" aria-describedby="portfolio-publish-help"
            onClick={() => { setPublished(!published); change(); }} />
        </div>
        <p id="portfolio-publish-help">공개하면 아래에서 선택한 예측만 공유 주소로 볼 수 있습니다. 비공개로 설정하면 공유 주소로도 볼 수 없습니다. 변경 사항은 저장 후 반영됩니다.</p>
        <div className="market-actions"><button onClick={save}>{busy ? "저장 중..." : dirty ? "변경 사항 저장" : "저장"}</button><button onClick={() => setPreview(!preview)} aria-pressed={preview}>미리보기</button></div>
        {url && <div className="portfolio-share"><label htmlFor="portfolio-url">공유 주소 {settings.published ? "(공개 중)" : "(현재 비공개)"}</label><input id="portfolio-url" value={url} readOnly onFocus={e => e.target.select()} /><div className="market-actions"><button onClick={copy}>URL 복사</button>{settings.published && <Link to={`/portfolio/${settings.shareId}`} target="_blank" rel="noopener noreferrer">공개 페이지 열기</Link>}</div></div>}
      </fieldset>
      {preview ? <section aria-label="포트폴리오 미리보기"><h2>미리보기 · 선택한 예측 {ids.length}개</h2><p className="portfolio-introduction">{introduction}</p><Entries items={selected} /></section> : <>
        <h2>공개할 예측 ({ids.length}/100)</h2><p>위에서부터 표시됩니다. 평균 매수가는 전체 매수 체결 기준입니다.</p>
        <p className="portfolio-note">공개 시 첫 번째 주제의 썸네일이 카카오톡 등에서 링크를 공유할 때 대표 이미지로 사용됩니다. 썸네일이 없으면 FactWeb 기본 이미지가 표시됩니다.</p>
        <ol className="portfolio-selection">{selected.map((item, index) => <li key={item.id}><span>{item.title}</span><div><button disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label={`${item.title} 위로`}>↑</button><button disabled={busy || index === ids.length - 1} onClick={() => move(index, 1)} aria-label={`${item.title} 아래로`}>↓</button><button disabled={busy} onClick={() => { setIds(ids.filter(id => id !== Number(item.id))); change(); }}>제외</button></div></li>)}</ol>
        <h2>등록 가능한 예측</h2>{!candidates.length && <p>아직 매수 체결 후 결과가 확정된 예측이 없습니다.</p>}
        <div className="market-grid">{candidates.map(item => <div key={item.id}><label className="portfolio-toggle"><input type="checkbox" checked={ids.includes(Number(item.id))} disabled={busy || (!ids.includes(Number(item.id)) && ids.length >= 100)} onChange={e => { setIds(e.target.checked ? [...ids, Number(item.id)] : ids.filter(id => id !== Number(item.id))); change(); }} /> 포트폴리오에 표시</label><Entry item={item} /></div>)}</div>
      </>}
    </>}
  </main>;
}
